import { useAuth } from '../../hooks/useAuth';

/**
 * PermissionRoute - Gates a route behind a required permission.
 * - While auth/permissions are loading → neutral placeholder (no flash of guarded content).
 * - Missing the permission → styled "You don't have access" panel (no redirect loop, no blank).
 * - System roles (e.g. Super Admin) hold every permission via hasPermission (isSystem).
 */

const RouteLoading = () => (
  <div
    aria-hidden="true"
    style={{
      minHeight: '60vh',
      margin: '2rem',
      borderRadius: '12px',
      background: 'var(--color-tan-50, #fefdfb)',
      border: '0.5px solid var(--color-tan-300, #f3e5d0)',
      opacity: 0.6
    }}
  />
);

const AccessDenied = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      minHeight: '60vh',
      padding: '2rem',
      gap: '0.5rem'
    }}
  >
    <div
      style={{
        fontSize: '1.25rem',
        fontWeight: 800,
        letterSpacing: '-0.025em',
        color: 'var(--color-charcoal, #2d2d2d)'
      }}
    >
      {"You don't have access to this page"}
    </div>
    <div
      style={{
        fontSize: '0.95rem',
        fontWeight: 400,
        color: 'var(--color-gray-500, #737373)',
        maxWidth: '420px',
        lineHeight: 1.5
      }}
    >
      {"Your role doesn't include permission to view this section. If you think this is a mistake, contact your administrator."}
    </div>
  </div>
);

const PermissionRoute = ({ permission, children }) => {
  const { hasPermission, isLoading } = useAuth();

  // No permission required = always render
  if (!permission) return children;

  // Auth/permissions still loading → neutral placeholder, never the guarded content
  if (isLoading) return <RouteLoading />;

  // Lacks the permission (including via a direct deep-link) → styled access-denied state.
  // No <Navigate> redirect: avoids redirect loops and blank screens; the URL stays put.
  if (!hasPermission(permission)) return <AccessDenied />;

  return children;
};

export default PermissionRoute;
